import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { PublicCourse } from '@/types/course'
import {
  courseLink,
  courseSearchFallback,
  findCourseByCodeOrKeywords,
  type StageId,
} from '@/config/careerPaths'
import { Container } from '@/components/ui'
import CourseComboPrice, { type GiftComboInfo } from '@/components/course/CourseComboPrice'

interface EaseStageCardsProps {
  courses: PublicCourse[]
  giftCombos?: GiftComboInfo[]
  loading?: boolean
}

interface PathCourseRef {
  code: string
  label: string
  keywords: string[]
}

interface PathStripItem {
  id: StageId
  label: string
  title: string
  description: string
  cta: string
  priority: 'core' | 'later'
  courses: PathCourseRef[]
}

/** Mỗi lộ trình map thẳng tới khóa cụ thể. */
const PATH_STRIP: PathStripItem[] = [
  {
    id: 'become-ba',
    label: 'BA thực chiến',
    title: 'Khởi đầu nghề BA',
    description:
      'Khóa BA thực chiến thời kỳ AI — từ mindset đến tài liệu, prototype và dự án thực tế.',
    cta: 'Bắt đầu hành trình BA ngay',
    priority: 'core',
    courses: [
      {
        code: 'BAAIONL',
        label: 'BA thực chiến thời kỳ AI',
        keywords: ['thực chiến', 'ba'],
      },
    ],
  },
  {
    id: 'systems',
    label: 'System Thinking',
    title: 'Làm chủ hệ thống',
    description:
      'API, Database và AI Coding — đọc hệ thống rõ, trao đổi với Dev và tự kiểm chứng kỹ thuật.',
    cta: 'Mở khóa cách BA nói chuyện với Dev',
    priority: 'core',
    courses: [
      {
        code: 'API',
        label: 'API for BA',
        keywords: ['api'],
      },
      {
        code: 'Database',
        label: 'Database for BA',
        keywords: ['database', 'dữ liệu'],
      },
      {
        code: 'AICODING',
        label: 'AI Coding',
        keywords: ['ai coding', 'coding'],
      },
    ],
  },
  {
    id: 'apply-ai',
    label: 'AI-First BA',
    title: 'Tăng tốc cùng AI',
    description:
      'AI4BA và AI Coding để tăng tốc tài liệu, tư duy giải pháp và dựng sản phẩm demo.',
    cta: 'Khám phá cách BA tăng tốc với AI',
    priority: 'core',
    courses: [
      {
        code: 'AI4BA',
        label: 'AI4BA',
        keywords: ['ai4ba'],
      },
      {
        code: 'AICODING',
        label: 'AI Coding',
        keywords: ['ai coding', 'coding'],
      },
    ],
  },
  {
    id: 'cert',
    label: 'Chứng chỉ BA',
    title: 'Chinh phục chứng chỉ',
    description:
      'ECBA · CCBA · CBAP — ôn sau khi đã vững kiến thức chính, bộ đề giống thi thật.',
    cta: 'Sẵn sàng chinh phục chứng chỉ quốc tế',
    priority: 'later',
    courses: [
      {
        code: 'Ccba_cbap',
        label: 'CCBA / CBAP',
        keywords: ['ccba', 'cbap', 'ecba'],
      },
    ],
  },
]

function resolvePathCourse(
  courses: PublicCourse[],
  ref: PathCourseRef,
  comboBySourceId: Map<number, GiftComboInfo>
): {
  label: string
  href: string
  resolved: boolean
  courseId?: number
  combo?: GiftComboInfo
} {
  const matched = findCourseByCodeOrKeywords(courses, ref.code, ref.keywords)
  if (matched) {
    return {
      label: matched.name || ref.label,
      href: courseLink(matched),
      resolved: true,
      courseId: matched.id,
      combo: comboBySourceId.get(matched.id),
    }
  }
  return {
    label: ref.label,
    href: courseSearchFallback(ref.label),
    resolved: false,
  }
}

const EaseStageCards = ({ courses, giftCombos = [], loading }: EaseStageCardsProps) => {
  const [featuredId, setFeaturedId] = useState<StageId>('become-ba')

  const comboBySourceId = useMemo(() => {
    const map = new Map<number, GiftComboInfo>()
    for (const c of giftCombos) {
      if (!map.has(c.sourceCourseId)) map.set(c.sourceCourseId, c)
    }
    return map
  }, [giftCombos])

  const resolvedByStage = useMemo(() => {
    const map = {} as Record<
      StageId,
      Array<{
        label: string
        href: string
        resolved: boolean
        courseId?: number
        combo?: GiftComboInfo
      }>
    >
    for (const item of PATH_STRIP) {
      map[item.id] = item.courses.map((ref) =>
        resolvePathCourse(courses, ref, comboBySourceId)
      )
    }
    return map
  }, [courses, comboBySourceId])

  return (
    <section className="eh-path-strip" id="stage" aria-labelledby="path-strip-title">
      <Container>
        <div className="eh-path-strip__top eh-reveal">
          <h2 id="path-strip-title" className="eh-path-strip__heading">
            Lộ trình,
            <br />
            theo mục tiêu.
          </h2>
          <p className="eh-path-strip__lead">
            Click vào từng khóa để vào trang chi tiết ngay — BA thực chiến, API, AI4BA, chứng chỉ…
            theo đúng lộ trình bạn chọn.
          </p>
        </div>
      </Container>

      <div className="eh-path-strip__grid" role="list">
        {PATH_STRIP.map((item) => {
          const featured = featuredId === item.id
          const pathCourses = resolvedByStage[item.id] || []
          const primary = pathCourses[0]

          return (
            <article
              key={item.id}
              role="listitem"
              tabIndex={0}
              className={`eh-path-card${featured ? ' is-featured' : ''}${
                item.priority === 'later' ? ' eh-path-card--later' : ''
              }`}
              onMouseEnter={() => setFeaturedId(item.id)}
              onFocus={() => setFeaturedId(item.id)}
              onClick={() => setFeaturedId(item.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setFeaturedId(item.id)
                }
              }}
              aria-current={featured ? 'true' : undefined}
            >
              <div className="eh-path-card__meta">
                <span className="eh-path-card__label">{item.label}</span>
                {item.priority === 'later' ? (
                  <span className="eh-path-card__badge">Sau kiến thức chính</span>
                ) : null}
              </div>

              <Link to={primary.href} className="eh-path-card__title-link">
                <h3 className="eh-path-card__title">{item.title}</h3>
              </Link>
              <p className="eh-path-card__desc">{item.description}</p>

              <div className="eh-path-card__courses-block">
                <p className="eh-path-card__courses-label">Khóa trong lộ trình</p>
                {loading ? (
                  <p className="eh-path-card__courses-empty">Đang tải khóa học…</p>
                ) : (
                  <ul className="eh-path-card__courses">
                    {pathCourses.map((course) => (
                      <li key={`${item.id}-${course.label}`}>
                        <Link to={course.href} className="eh-path-card__course-link">
                          <span className="min-w-0 flex-1">
                            <span className="block">{course.label}</span>
                            {course.combo ? (
                              <CourseComboPrice
                                combo={course.combo}
                                variant="compact"
                                className="mt-1"
                              />
                            ) : null}
                          </span>
                          <span className="eh-path-card__course-action shrink-0">
                            Chi tiết <span aria-hidden>→</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <Link to={primary.href} className="eh-path-card__link">
                {item.cta}
                <span className="eh-arrow" aria-hidden>
                  →
                </span>
              </Link>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default EaseStageCards
